<template>
  <section
    id="wofuer"
    class="stores-wofuer"
  >
    <GridContainer
      padding
      class="stores-wofuer__container"
    >
      <div class="stores-wofuer__header">
        <div class="stores-wofuer__header-content">
          <p class="stores-wofuer__eyebrow">Wofür du zahlst</p>
          <Heading
            tag="h2"
            size="lg"
            spaces-bottom-rem
          >
            Einmal eingerichtet, <span class="text--primary"> überall im Store zahlen.</span>
          </Heading>
          <Paragraph
            is-size-big
            class="stores-wofuer__lead"
          >
            Hinterleg die Handyrechnung einmal in deinem App Store oder Google Play Store, danach
            kannst du damit jede App, jedes Abo und jeden In-App-Kauf bezahlen. Ohne Karte, ohne
            Konto.
          </Paragraph>

          <ul class="stores-wofuer__checks">
            <li>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M6 2h12v20l-3-2-3 2-3-2-3 2zM9 7h6M9 11h6M9 15h4" />
              </svg>
              Über die Telekom Rechnung
            </li>
            <li>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <line
                  x1="4"
                  y1="8"
                  x2="20"
                  y2="8"
                />
                <circle
                  cx="9"
                  cy="8"
                  r="2.6"
                  fill="currentColor"
                />
                <line
                  x1="4"
                  y1="16"
                  x2="20"
                  y2="16"
                />
                <circle
                  cx="15"
                  cy="16"
                  r="2.6"
                  fill="currentColor"
                />
              </svg>
              Kontrolle über Partnerdienste
            </li>
            <li>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <rect
                  x="2"
                  y="5"
                  width="20"
                  height="14"
                  rx="2"
                />
                <path d="M2 10h20M3 3l18 18" />
              </svg>
              Keine Bankdaten nötig
            </li>
          </ul>

          <Paragraph class="stores-wofuer__note">
            Falls die Zahlung bei dir gesperrt ist, gibst du sie einmal in der MeinMagenta App im
            Mehr-Menü unter „Drittanbietersperre“ frei.
          </Paragraph>
        </div>
        <LogoWall
          class="stores-wofuer__header-image"
          :rows="logoWall4Rows"
        />
      </div>

      <Card
        class="stores-wofuer__setup"
        size="small"
      >
        <ToggleButton
          :is-active="isAndroid"
          class="stores-wofuer__store-toggle"
          toggle-aria-label-left="Schritte für App Store anzeigen"
          toggle-aria-label-right="Schritte für Google Play anzeigen"
          @update:toggle="isAndroid = $event"
        >
          <template #left>
            <CdnImage
              src="/content/content-images/dcb/stores/54becf0ade1d949e0b42df8bd4f78381/logo-appstore.svg"
              alt="App Store"
              aspect-ratio="1"
              sizes="xs:24px"
              lazy
              render-without-figure
            />
            App Store
          </template>
          <template #right>
            <CdnImage
              src="/content/content-images/dcb/stores/a142233a0b9d83cb884c8f949ee2f420/logo-googleplay.svg"
              alt="Google Play"
              aspect-ratio="1"
              sizes="xs:24px"
              lazy
              render-without-figure
            />
            Google Play
          </template>
        </ToggleButton>

        <div class="stores-wofuer__steps">
          <Card
            v-for="(card, i) in activeSteps"
            :key="i"
            v-bind="card"
            size="small"
            class="stores-wofuer__step-card"
          />
        </div>

        <div class="stores-wofuer__video">
          <Heading
            tag="h3"
            size="xs"
            spaces-bottom-rem
            class="text--uppercase text--bold text--primary"
          >
            Als Video ansehen
          </Heading>
          <YoutubeVideo :video-id="activeVideo.id">
            <template #placeholder>
              <CdnImage
                :src="activeVideo.thumbnail"
                :alt="activeVideo.caption"
                aspect-ratio="16 / 9"
                sizes="xs:100vw sm:560px"
                render-without-figure
                lazy
              />
            </template>
          </YoutubeVideo>
        </div>

        <div class="stores-wofuer__recap">
          <Heading
            tag="h3"
            size="xs"
            spaces-bottom-rem
            class="text--uppercase text--bold text--primary"
          >
            Ausführliche Anleitung
          </Heading>
          <Paragraph> Alle Schritte im Detail findest du hier erklärt: </Paragraph>
          <LinkButton
            tag="button"
            type="button"
            variant="outline-primary"
            label="So richtest du das ein"
            @click="openManual"
          />
        </div>
      </Card>

      <Paragraph class="stores-wofuer__footnote">
        Store-Zahlung per Telekom Handyrechnung. Die genauen Einrichtungsschritte können je Gerät
        und Betriebssystem-Version abweichen.
      </Paragraph>
    </GridContainer>
  </section>
</template>

<script setup lang="ts">
import {
  GridContainer,
  Heading,
  Paragraph,
  Card,
  LinkButton,
  ToggleButton,
  YoutubeVideo,
  detectDevice,
  type CardComponentProps,
} from '@i22team/td-longtail-vue-components';
import CdnImage from '~/components/cdn-image.vue';
import LogoWall from '~/components/dcb/landingpage/logo-wall.vue';
import { openAnleitung, partnerApple, partnerGoogle } from '~/lib/dcb/dcb-landingpage-partners';
import { logoWall4Rows } from '~/components/dcb/stores/app-icons';
import { appStore, googlePlay } from '../qr-code-links';

const device = detectDevice.useDetectDevice();

const isAndroid = ref(false);

watch(
  device,
  () => {
    if (device.value === 'Android') {
      isAndroid.value = true;
    }
  },
  { immediate: true }
);

const iosSteps: CardComponentProps[] = [
  {
    squareBadgeReverse: true,
    squareBadge: [{ text: '#1', theme: 'magenta' }],
    label: {
      customClass: 'text--mb text--uppercase text--primary text--bold',
      text: 'Schritt 1',
    },
    heading: {
      tag: 'h3',
      size: 'sm',
      text: 'Zahlung & Versand öffnen',
      spacesBottomRem: true,
    },
    text: 'Einstellungen → [Dein Name] → Zahlung & Versand.',
    image: {
      src: '/content/content-images/dcb/stores/wofuer/4c8cb726a59be57b7dd9cd0ee4e11c83/mockup-ios-set-step.png',
      aspectRatio: '1359 / 1895',
    },
    buttons: [
      {
        tag: 'a',
        href: appStore,
        isExternal: true,
        target: '_blank',
        text: true,
        label: 'Für iOS/Apple',
      },
    ],
  },
  {
    squareBadgeReverse: true,
    squareBadge: [{ text: '#2', theme: 'magenta' }],
    label: {
      customClass: 'text--mb text--uppercase text--primary text--bold',
      text: 'Schritt 2',
    },
    heading: {
      tag: 'h3',
      size: 'sm',
      text: 'Zahlungsmethode hinzufügen',
      spacesBottomRem: true,
    },
    image: {
      src: '/content/content-images/dcb/stores/wofuer/2d0765a833278498430d1668487c8c31/mockup-ios-pay-step.png',
      aspectRatio: '1359 / 1895',
    },
    text: 'Zahlungsmethode hinzufügen und „Handyrechnung“ (Telekom) wählen.',
  },
];

const androidSteps: CardComponentProps[] = [
  {
    squareBadgeReverse: true,
    squareBadge: [{ text: '#1', theme: 'magenta' }],
    label: {
      customClass: 'text--mb text--uppercase text--primary text--bold',
      text: 'Schritt 1',
    },
    heading: {
      tag: 'h3',
      size: 'sm',
      text: 'Zahlungsmethoden öffnen',
      spacesBottomRem: true,
    },
    image: {
      src: '/content/content-images/dcb/stores/wofuer/e912b796d9eec3d1b4e02a300dd268d8/mockup-android-set-step.png',
      aspectRatio: '1359 / 1895',
    },
    text: 'Play-App → Profilbild → Zahlungen und Abos → Zahlungsmethoden.',
    buttons: [
      {
        tag: 'a',
        href: googlePlay,
        isExternal: true,
        target: '_blank',
        text: true,
        label: 'Für Android/Google',
      },
    ],
  },
  {
    squareBadgeReverse: true,
    squareBadge: [{ text: '#2', theme: 'magenta' }],
    label: {
      customClass: 'text--mb text--uppercase text--primary text--bold',
      text: 'Schritt 2',
    },
    heading: {
      tag: 'h3',
      size: 'sm',
      text: 'Zahlungsmittel hinzufügen',
      spacesBottomRem: true,
    },
    image: {
      src: '/content/content-images/dcb/stores/wofuer/b8837b4d0117db9632ec56d3f6436469/mockup-android-pay-step.png',
      aspectRatio: '1359 / 1895',
    },

    text: 'Zahlungsmittel hinzufügen, dann „Telekom - Zahl einfach per Handyrechnung“ wählen.',
  },
];

const activeSteps = computed(() => (isAndroid.value ? androidSteps : iosSteps));

const videos = {
  ios: {
    id: '1EHsyc5c3RQ',
    thumbnail:
      '/content/content-images/dcb/stores/5bf38721f1d8bbc41a38bc044b790e13/video-thumb-ios.jpg',
    caption: 'Offizielles Telekom Video · iOS',
  },
  android: {
    id: 'm0qSVvvTXrw',
    thumbnail:
      '/content/content-images/dcb/stores/aef03c5ee8c43086695c3503c2fad90f/video-thumb-android.jpg',
    caption: 'Offizielles Telekom Video · Android',
  },
};

const activeVideo = computed(() => (isAndroid.value ? videos.android : videos.ios));

const openManual = () => {
  const partner = isAndroid.value ? partnerGoogle : partnerApple;
  if (!partner) return;
  openAnleitung(partner);
};
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.stores-wofuer {
  padding-block: 3rem 2rem;
  background-color: var(--color-gray-100);
  border-top-left-radius: 44px 44px;
  border-top-right-radius: 44px 44px;

  @include mq($mq-md) {
    padding-block: 4rem 2rem;
  }

  &__header {
    margin-bottom: 3rem;

    @include mq($mq-md) {
      display: grid;
      grid-template-columns: 2fr 3fr;
      gap: 3.5rem;
    }
  }

  &__header-content {
    @include mq($mq-md) {
      flex: 0 0 33%;
    }
  }

  &__header-image {
    --logo-wall-img-w: 100%;
    --logo-wall-img-h: 100%;
    @include mq($mq-md) {
      flex: 1 1 auto;
    }
  }

  &__eyebrow {
    font-size: 20px;
    font-weight: 700;
    text-transform: uppercase;
    color: var(--color-primary);
    margin-bottom: 12px;
  }

  &__lead {
    margin-inline: auto;
    margin-bottom: 1.5rem;
  }

  &__checks {
    list-style: none;
    padding: 0;
    margin: 0 auto 1.5rem;
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;

    li {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 600;

      > svg {
        color: var(--color-primary);
        width: 22px;
        flex-shrink: 0;
      }
    }
  }

  &__note {
    margin: 0 auto 2rem;
    font-size: 14px;
    color: var(--color-font-subtle);
  }

  &__setup {
    --card-bg-color: #fff;

    margin-inline: auto;
    text-align: center;
    margin-bottom: 3rem;
  }

  &__store-toggle {
    margin-bottom: 2rem;
    position: sticky;
    top: calc(var(--nav-height-scrolled) + 6px);
    z-index: 6;
    --btn-color-background-custom: #fff;

    :deep(.toggle-button__label) {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    :deep(img) {
      width: 1.5rem;
      height: 1.5rem;
      margin-bottom: -3px;
    }
  }

  &__steps {
    display: grid;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
    text-align: left;

    @include mq($mq-md) {
      margin-bottom: 3rem;
      grid-template-columns: repeat(2, 1fr);
      max-width: 900px;
      margin-inline: auto;
    }
  }

  &__step-card {
    --card-bg-color: #fff;
    border: 1px solid var(--color-gray-200);
    --card-image-container-height: 410px;
    --card-image-flex: 0 0 410px;
    --card-image-padding: 1rem 1rem 0;
    --card-action-justify: center;
    text-align: center;

    @include mq($mq-md) {
      --card-image-container-height: 460px;
      --card-image-flex: 0 0 460px;
    }
  }

  &__video {
    max-width: 560px;
    margin-inline: auto;
    margin-bottom: 1.5rem;
    @include mq($mq-md) {
      margin-bottom: 3rem;
    }

    :deep(.youtube-video__video) {
      min-height: 250px;
      border-radius: 22px;
      overflow: hidden;
    }
  }

  &__video-caption {
    font-weight: 700;
    margin: 0.75rem 0 0.25rem;
  }

  &__video-consent {
    font-size: 13px;
    color: var(--color-font-subtle);
    margin: 0;
  }

  &__recap {
    max-width: 480px;
    margin: 0 auto 1.5rem;
    text-align: center;
  }

  &__recap-list {
    list-style: none;
    counter-reset: recap;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    text-align: left;

    li {
      counter-increment: recap;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-weight: 600;

      &::before {
        content: counter(recap);
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        border-radius: 50%;
        background: var(--color-primary);
        color: var(--color-bg);
        font-size: 13px;
        font-weight: 700;
      }
    }
  }

  &__footnote {
    margin-inline: auto;
    text-align: center;
    font-size: 15px;
    color: var(--color-font-subtle);
  }
}
</style>
